import { useState, useEffect, useCallback } from 'react';
import { storageService, STORAGE_KEYS } from '@/services/storageService';
import { CURRENT_VERSION } from '@/lib/changelog';

interface UseWhatsNewReturn {
  hasNewVersion: boolean;
  isLoading: boolean;
  dismissVersion: () => Promise<void>;
}

/**
 * Hook to manage "What's New" visibility
 * Compares current version with last seen version in localStorage
 */
export function useWhatsNew(): UseWhatsNewReturn {
  const [lastSeenVersion, setLastSeenVersion] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadLastSeenVersion = async () => {
      try {
        const stored = await storageService.getItem(STORAGE_KEYS.LAST_SEEN_VERSION);
        setLastSeenVersion(stored);
      } catch (error) {
        console.error('Failed to load last seen version:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadLastSeenVersion();
  }, []);

  const hasNewVersion = !isLoading && lastSeenVersion !== CURRENT_VERSION;

  const dismissVersion = useCallback(async () => {
    try {
      await storageService.setItem(STORAGE_KEYS.LAST_SEEN_VERSION, CURRENT_VERSION);
      setLastSeenVersion(CURRENT_VERSION);
    } catch (error) {
      console.error('Failed to save version:', error);
    }
  }, []);

  return {
    hasNewVersion,
    isLoading,
    dismissVersion,
  };
}
